/**
 * src/components/Financial/modules/GannFanModule.jsx
 * 【修正版：ReferenceError解消・計算最適化済】
 */
import React, { useMemo } from "react"; // useMemo を追加
import { TrendingUp, Info } from "lucide-react";

/**
 * 描画レイヤー (React.memo と useMemo による高速描画版)
 */
/**
 * 描画レイヤー (クリッピング・境界判定ラベル・完全版)
 */
/**
 * 描画レイヤー (ラベル配置・角度プロパティ修正・完全版)
 */
export const GannFanLayer = React.memo(function (props) {
  var show = props.show;
  var baseIndex = props.baseIndex;
  var basePrice = props.basePrice;
  var pointsPerUnit = props.pointsPerUnit;
  var direction = props.direction;
  var coords = props.coords;
  var displayRange = props.displayRange;
  var angleConfigs = props.angleConfigs;
  var timeline = props.timeline;
  var isDarkMode = props.isDarkMode;

  if (
    show === false ||
    baseIndex === null ||
    basePrice === null ||
    !coords ||
    !displayRange ||
    !timeline ||
    !timeline[baseIndex]
  ) {
    return null;
  }

  // PPUを数値として確実に処理
  var ppu = parseFloat(pointsPerUnit);
  if (isNaN(ppu) || ppu <= 0) return null;

  return useMemo(
    function () {
      var x0 = coords.getX(baseIndex - displayRange.start);
      var y0 = coords.getY(basePrice);
      var pad = coords.pad || { l: 60, t: 80, r: 80, b: 40 };
      var iH = coords.iH || 500;
      var topLimit = pad.t;
      var bottomLimit = pad.t + iH;

      var endIdx = Math.min(
        timeline.length - 1,
        displayRange.start + displayRange.count,
      );

      return (
        <g className="gann-fan-layer">
          {angleConfigs.map(function (config, idx) {
            if (config.enabled === false) return null;

            // プロパティ名の不一致を解消 (slopeを使用)
            var currentSlope = config.slope !== undefined ? config.slope : 1.0;
            var stepPrice = ppu * currentSlope;
            var hitPoints = [];

            // 1. 各バーにおける接触判定の計算
            var startLoopIdx = Math.max(0, displayRange.start);
            for (var i = startLoopIdx; i <= endIdx; i++) {
              var node = timeline[i];
              if (!node) continue;
              var dx = i - baseIndex;
              var targetPrice =
                direction === "up"
                  ? basePrice + stepPrice * dx
                  : basePrice - stepPrice * dx;
              var yPos = coords.getY(targetPrice);

              var high = node.High !== undefined ? node.High : node.price || 0;
              var low = node.Low !== undefined ? node.Low : node.price || 0;
              if (high >= targetPrice && low <= targetPrice) {
                hitPoints.push({
                  x: coords.getX(i - displayRange.start),
                  y: yPos,
                });
              }
            }

            // 2. ラベル位置の幾何学的算出 (境界交点ロジック)
            var rightLimit = coords.pad.l + (coords.iW || 1000);
            var lastAbsDx = endIdx - baseIndex;
            var endPrice =
              direction === "up"
                ? basePrice + stepPrice * lastAbsDx
                : basePrice - stepPrice * lastAbsDx;

            var xEndRaw = coords.getX(endIdx - displayRange.start);
            var yEndRaw = coords.getY(endPrice);

            // ラインの傾き(m)を算出
            var m = xEndRaw - x0 !== 0 ? (yEndRaw - y0) / (xEndRaw - x0) : 0;

            // デフォルトのラベル位置
            var labelX = xEndRaw;
            var labelY = yEndRaw;
            var textAnchor = "start";
            var baseline = "middle";

            // A. 右端での衝突判定 (価格軸への重なり防止)
            var yAtRight = m * (rightLimit - x0) + y0;
            if (
              xEndRaw >= rightLimit ||
              (yAtRight >= topLimit && yAtRight <= bottomLimit)
            ) {
              labelX = rightLimit - 5; // 右端から5px内側に
              labelY = yAtRight;
              textAnchor = "end";
            }

            // B. 上端・下端での衝突判定 (8x1などの急なライン用)
            if (labelY < topLimit) {
              // 上端境界との交点Xを算出
              labelY = topLimit + 12;
              if (m !== 0) labelX = (topLimit - y0) / m + x0;
              labelX = Math.max(x0, Math.min(rightLimit - 10, labelX));
              textAnchor = "middle";
              baseline = "hanging";
            } else if (labelY > bottomLimit) {
              // 下端境界との交点Xを算出
              labelY = bottomLimit - 12;
              if (m !== 0) labelX = (bottomLimit - y0) / m + x0;
              labelX = Math.max(x0, Math.min(rightLimit - 10, labelX));
              textAnchor = "middle";
              baseline = "alphabetic";
            }

            return (
              <g key={"gann-ang-grp-" + idx}>
                {/* ライン描画 (clipPathで枠外をカット) */}
                <line
                  x1={x0}
                  y1={y0}
                  x2={xEndRaw}
                  y2={yEndRaw}
                  stroke={config.color}
                  strokeWidth={isDarkMode ? 1.2 : 1}
                  strokeDasharray={currentSlope === 1 ? "none" : "3,2"}
                  opacity={0.5}
                  clipPath="url(#chart-content-clip)"
                />

                {/* 角度ラベル (計算された境界位置に配置) */}
                <text
                  x={labelX}
                  y={labelY}
                  fill={config.color}
                  fontSize="10px"
                  fontWeight="900"
                  textAnchor={textAnchor}
                  dominantBaseline={baseline}
                  style={{
                    userSelect: "none",
                    paintOrder: "stroke",
                    stroke: isDarkMode
                      ? "rgba(0,0,0,0.8)"
                      : "rgba(255,255,255,0.8)",
                    strokeWidth: "3px",
                  }}
                >
                  {config.label}
                </text>

                {/* ヒットポイント */}
                {hitPoints.slice(0, 50).map(function (p, pIdx) {
                  return (
                    <circle
                      key={"hit-" + idx + "-" + pIdx}
                      cx={p.x}
                      cy={p.y}
                      r="2.5"
                      fill="none"
                      stroke={config.color}
                      strokeWidth="1"
                      opacity={0.4}
                    />
                  );
                })}
              </g>
            );
          })}
        </g>
      );
    },
    [
      show,
      baseIndex,
      basePrice,
      ppu,
      direction,
      coords,
      displayRange,
      angleConfigs,
      timeline,
      isDarkMode,
    ],
  );
});

/**
 * src/components/Financial/modules/GannFanModule.jsx
 * 【デザイン統一版：ON/OFFボタンおよび各種切替ボタンのスタイル修正】
 */
export const GannFanSettingsUI = function (props) {
  var show = props.show;
  var setShow = props.setShow; // onShowChange から変更
  var pointsPerUnit = props.pointsPerUnit;
  var setPointsPerUnit = props.setPointsPerUnit; // onPPUChange から変更
  var direction = props.direction;
  var setDirection = props.setDirection; // onDirectionChange から変更
  var angleConfigs = props.angleConfigs;
  var setAngleConfigs = props.setAngleConfigs;
  var calcMode = props.calcMode;
  var setCalcMode = props.setCalcMode; // onCalcModeChange から変更
  var isDarkMode = props.isDarkMode;

  // 1. テーマ定義を他モジュールと統一
  var theme = {
    cardBg: isDarkMode ? "#1c1f26" : "#f8fafc",
    border: isDarkMode ? "#2d313d" : "#e2e8f0",
    text: isDarkMode ? "#ffffff" : "#1e293b",
    subText: isDarkMode ? "rgba(255, 255, 255, 0.4)" : "#64748b",
    inputBg: isDarkMode ? "#0b0e14" : "#ffffff",
  };

  /**
   * 2. ボタン共通スタイル (他モジュールと完全に統一)
   * 修正点: 横パディングを20pxに拡大、OFF時の背景色を #2d2c33 に、境界線を透過白に変更
   */
  var btnSt = function (active) {
    return {
      padding: "6px 20px", // 統一パディング
      fontSize: "11px",
      fontWeight: "bold",
      borderRadius: "4px",
      cursor: "pointer",
      // 背景色：アクティブ時は青、非アクティブ時は他モジュール同様の濃いグレー (#2d2c33)
      background: active ? "#2c2d96" : isDarkMode ? "#2d2c33" : "#ffffff",
      // 文字色：非アクティブ時は明るいグレー (#ccc)
      color: active ? "#fff" : isDarkMode ? "#ccc" : "#64748b",
      // 境界線：非アクティブ時は透過白 (rgba(255,255,255,0.1))
      border:
        "1px solid " +
        (active ? "#2c2d96" : isDarkMode ? "rgba(255,255,255,0.1)" : "#cbd5e1"),
      transition: "all 0.2s",
    };
  };

  return (
    <div
      style={{
        background: theme.cardBg,
        padding: "20px",
        borderRadius: "12px",
        border: "1px solid " + theme.border,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <div
          style={{
            fontSize: "15px",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: theme.text,
          }}
        >
          <TrendingUp size={18} color={isDarkMode ? "#ffffff" : "#2c2d96"} />
          <span>Gann Fan Angle(ギャン・ファン・アングル)</span>
        </div>

        {/* 修正：統一されたデザインの ON/OFF ボタン */}
        <button
          onClick={function () {
            // 親から渡された setShow を呼び出すように修正
            if (typeof setShow === "function") {
              setShow(!show);
            }
          }}
          style={btnSt(show)}
        >
          {show ? "ON" : "OFF"}
        </button>
      </div>

      <div
        style={{
          opacity: show ? 1 : 0.5,
          pointerEvents: show ? "auto" : "none",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-end",
            gap: "25px",
            marginBottom: "25px",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ fontSize: "11px", color: theme.subText }}>
              計算モード
            </label>
            <div style={{ display: "flex", gap: "4px" }}>
              <button
                onClick={function () {
                  setCalcMode("bar");
                }}
                style={btnSt(calcMode === "bar")}
              >
                営業日
              </button>
              <button
                onClick={function () {
                  setCalcMode("time");
                }}
                style={btnSt(calcMode === "time")}
              >
                暦日
              </button>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ fontSize: "11px", color: theme.subText }}>
              1x1 スケール (Pts/Unit)
            </label>
            <input
              type="number"
              step="any"
              value={pointsPerUnit}
              onChange={function (e) {
                // 文字列ではなく数値としてセットすることで計算を即座に反映させる
                var val = parseFloat(e.target.value);
                if (!isNaN(val)) {
                  setPointsPerUnit(val);
                } else if (e.target.value === "") {
                  setPointsPerUnit("");
                }
              }}
              style={{
                padding: "8px 12px",
                border: "1px solid " + theme.border,
                borderRadius: "6px",
                width: "120px",
                fontSize: "12px",
                background: theme.inputBg,
                color: theme.text,
                outline: "none",
              }}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ fontSize: "11px", color: theme.subText }}>
              方向
            </label>
            <div style={{ display: "flex", gap: "4px" }}>
              <button
                onClick={function () {
                  setDirection("up");
                }}
                style={btnSt(direction === "up")}
              >
                上昇
              </button>
              <button
                onClick={function () {
                  setDirection("down");
                }}
                style={btnSt(direction === "down")}
              >
                下降
              </button>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <label style={{ fontSize: "11px", color: theme.subText }}>
            表示アングル設定（カラー連動）
          </label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
            {angleConfigs.map(function (fan, i) {
              return (
                <div
                  key={"cfg-" + i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    background: fan.enabled
                      ? isDarkMode
                        ? "rgba(255,255,255,0.05)"
                        : "#fff"
                      : "transparent",
                    padding: "6px 12px",
                    borderRadius: "8px",
                    border:
                      "1px solid " + (fan.enabled ? fan.color : theme.border),
                  }}
                >
                  <input
                    type="checkbox"
                    checked={fan.enabled}
                    onChange={function () {
                      var next = angleConfigs.slice();
                      next[i] = Object.assign({}, next[i], {
                        enabled: !next[i].enabled,
                      });
                      setAngleConfigs(next);
                    }}
                    style={{ cursor: "pointer" }}
                  />
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: "bold",
                      color: fan.enabled ? theme.text : theme.subText,
                    }}
                  >
                    {fan.label}
                  </span>
                  <input
                    type="color"
                    value={fan.color}
                    onChange={function (e) {
                      var next = angleConfigs.slice();
                      next[i] = Object.assign({}, next[i], {
                        color: e.target.value,
                      });
                      setAngleConfigs(next);
                    }}
                    style={{
                      width: "18px",
                      height: "18px",
                      border: "none",
                      padding: 0,
                      background: "none",
                      cursor: "pointer",
                    }}
                    disabled={!fan.enabled}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          color: theme.subText,
          fontSize: "10px",
          marginTop: "20px",
          paddingTop: "15px",
          borderTop: "1px solid " + theme.border,
        }}
      >
        <Info size={14} />
        <span>
          基準点をダブルクリックで設定。1x1スケールに基づき、主要な幾何学的角度ラインを描画します。
        </span>
      </div>
    </div>
  );
};
